import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Upload } from "antd";
import type { UploadChangeParam, UploadFile, UploadProps } from "antd/es/upload/interface";
import type React from "react";
import { useEffect, useState } from "react";
import apiClient from "@/app/api/client";
import { fileEndpoints } from "@/app/api/endpoints";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";

interface ImageUploadProps {
	value?: string | null;
	onChange?: (fileId: string | null) => void;
	disabled?: boolean;
	initialUrl?: string | null;
	style?: React.CSSProperties;
}

export function ImageUpload({ value, onChange, disabled, initialUrl, style }: ImageUploadProps) {
	const [loading, setLoading] = useState(false);
	const [imageUrl, setImageUrl] = useState<string | null>(initialUrl ?? null);
	const { message } = App.useApp();

	useEffect(() => {
		if (!value) {
			setImageUrl(initialUrl ?? null);
		}
	}, [value, initialUrl]);

	const handleChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
		if (info.file.status === "uploading") {
			setLoading(true);
			return;
		}
		if (info.file.status === "done") {
			setLoading(false);
			const response = info.file.response;
			if (response?.success) {
				const url = response?.data?.url ?? response?.url;
				const fileId = response?.data?.fileId ?? response?.data?.id;
				if (url) {
					setImageUrl(url);
				}
				if (fileId) {
					onChange?.(fileId);
				}
			} else {
				message.error("Rasm yuklashda xatolik yuz berdi");
			}
		} else if (info.file.status === "error") {
			setLoading(false);
			message.error("Rasm yuklashda xatolik yuz berdi");
		}
	};

	const beforeUpload = (file: File) => {
		const isJpgOrPng =
			file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
		if (!isJpgOrPng) {
			message.error("Faqat JPG/PNG/WEBP formatidagi rasmlarni yuklash mumkin!");
		}
		const isLt2M = file.size / 1024 / 1024 < 5;
		if (!isLt2M) {
			message.error("Rasm hajmi 5MB dan oshmasligi kerak!");
		}
		return isJpgOrPng && isLt2M;
	};

	const customRequest: UploadProps["customRequest"] = async ({ file, onSuccess, onError }) => {
		const formData = new FormData();
		formData.append("file", file as File);
		formData.append("type", "PHOTO");

		try {
			const res = await apiClient.post(fileEndpoints.upload, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			onSuccess?.(res.data);
		} catch (error) {
			onError?.(error as Error);
		}
	};

	const uploadButton = (
		<div>
			{loading ? <LoadingOutlined /> : <PlusOutlined />}
			<div style={{ marginTop: 8 }}>Rasm yuklash</div>
		</div>
	);

	return (
		<Upload
			name="file"
			listType="picture-card"
			className="avatar-uploader"
			showUploadList={false}
			beforeUpload={beforeUpload}
			onChange={handleChange}
			customRequest={customRequest}
			disabled={disabled || loading}
			style={style}
		>
			{imageUrl ? (
				<img
					src={resolveImageUrl(imageUrl)}
					alt="avatar"
					style={{
						width: "100%",
						height: "100%",
						objectFit: "contain",
						borderRadius: "8px",
						background: "#f5f5f5",
					}}
				/>
			) : (
				uploadButton
			)}
		</Upload>
	);
}
